import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getApiUrl, getApiBaseUrl } from '../utils/api';

interface SimpleAppState {
  // 인증 상태
  isAuthenticated: boolean;
  user: {
    id: number;
    username: string;
    name: string;
    role: 'admin' | 'operator';
    company_id?: number;
    company_name?: string;
  } | null;
  
  // 로딩 및 에러
  loading: boolean;
  error: string | null;

  // 선택된 운수사
  selectedCompanyId: number | null;
  selectedCompanyName: string | null;

  // 액션
  setLoading: (loading: boolean) => void;
  setError: (error: string | null) => void;
  setSelectedCompany: (companyId: number | null, companyName: string | null) => void;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
}

export const useSimpleStore = create<SimpleAppState>()(
  persist(
    (set) => ({
      // 초기 상태
      isAuthenticated: false,
      user: null,
      loading: false,
      error: null,
      selectedCompanyId: null,
      selectedCompanyName: null,

      // 액션
      setLoading: (loading: boolean) => set({ loading }),
      setError: (error: string | null) => set({ error }),
      setSelectedCompany: (companyId: number | null, companyName: string | null) => 
        set({ selectedCompanyId: companyId, selectedCompanyName: companyName }),

      login: async (username: string, password: string) => {
        set({ loading: true, error: null });
        
        try {
          const loginUrl = getApiUrl('/api/auth/login');
          console.log('로그인 시도:', { username, url: loginUrl });

          // 실제 API 호출
          const response = await fetch(loginUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
          });

          console.log('응답 상태:', response.status, response.statusText);

          // HTTP 에러 체크
          if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
          }

          const data = await response.json();
          console.log('응답 데이터:', data);

          if (data.success && data.data) {
            // 토큰을 localStorage에 저장
            if (data.data.token) {
              localStorage.setItem('adminToken', data.data.token);
            }

            // operator인 경우 자동으로 운수사 선택
            const companyId = data.data.user.company_id || null;
            const companyName = data.data.user.company_name || null;
            
            // operator인 경우 자신의 운수사를 자동 선택
            const selectedCompanyId = data.data.user.role === 'operator' ? companyId : null;
            const selectedCompanyName = data.data.user.role === 'operator' ? companyName : null;
            
            set({ 
              isAuthenticated: true, 
              user: data.data.user, 
              loading: false,
              error: null,
              selectedCompanyId,
              selectedCompanyName
            });
            console.log('로그인 성공');
            return true;
          } else {
            const errorMsg = data.error || '로그인에 실패했습니다.';
            console.error('로그인 실패:', errorMsg);
            set({ 
              loading: false, 
              error: errorMsg 
            });
            return false;
          }
        } catch (error: any) {
          console.error('로그인 에러:', error);
          let errorMsg = '알 수 없는 오류가 발생했습니다.';

          if (error.message.includes('Failed to fetch')) {
            errorMsg = `서버에 연결할 수 없습니다. 백엔드 서버(${getApiBaseUrl()})가 실행 중인지 확인해주세요.`;
          } else if (error.message.includes('HTTP')) {
            errorMsg = `서버 오류: ${error.message}`;
          } else {
            errorMsg = error.message || errorMsg;
          }
          
          set({ 
            loading: false, 
            error: errorMsg
          });
          return false;
        }
      },

      logout: () => {
        // localStorage에서 토큰 제거
        localStorage.removeItem('adminToken');
        
        set({ 
          isAuthenticated: false, 
          user: null, 
          error: null 
        });
      }
    }),
    {
      name: 'bus-auth-storage', // localStorage 키 이름
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        selectedCompanyId: state.selectedCompanyId,
        selectedCompanyName: state.selectedCompanyName
      }), // 인증 상태와 선택된 운수사 정보를 저장 (loading, error는 제외)
    }
  )
);